import {
  DiscoveryApi,
  IdentityApi,
  OAuthApi,
} from '@backstage/core-plugin-api';
import { Entity, EntityLink } from '@backstage/catalog-model';
import {
  ADORequestMiddleware,
  IADORequestMiddleware,
} from '../AdoRequestMiddleware';
import { ADOUser, WorkItem } from '../types';

/**
 * Interface defining common Azure DevOps service methods.
 */
export interface ICommonAdoService {
  /**
   * Fetches the list of team members who have a day off for a given Azure DevOps project.
   *
   * @param project - The Backstage entity representing the Azure DevOps project.
   * @returns A promise resolving to an array of `ADOUser` objects representing team members with their day-off details.
   */
  getTeamMembersDayOff(project: Entity): Promise<ADOUser[]>;

  /**
   * Fetches work items for a given Azure DevOps project based on a query.
   *
   * @param project - The Backstage entity representing the Azure DevOps project.
   * @param limit - The maximum number of work items to fetch. Defaults to 50.
   * @returns A promise resolving to an array of `WorkItem` objects representing the work items fetched.
   */
  getWorkItemsByQuery(project: Entity, limit?: number): Promise<WorkItem[]>;
}

/**
 * Implementation of the common Azure DevOps service methods.
 */
export class CommonAdoService implements ICommonAdoService {
  private readonly discoveryApi: DiscoveryApi;
  private readonly azDevOpsPluginKey = 'azdevops';
  private readonly adoMiddleware: IADORequestMiddleware;

  /**
   * Constructs an instance of `CommonAdoService`.
   *
   * @param options - Configuration options for the service, including APIs for discovery, authentication, and identity.
   * @param adoApiId - The identifier for the Azure DevOps API instance.
   */
  constructor(
    options: {
      discoveryApi: DiscoveryApi;
      authApi: OAuthApi;
      identityApi: IdentityApi;
    },
    adoApiId: string,
  ) {
    this.discoveryApi = options.discoveryApi;
    this.adoMiddleware = new ADORequestMiddleware({
      identityApi: options.identityApi,
      authApi: options.authApi,
      adoApiId: adoApiId,
    });
  }

  /**
   * Fetches the list of team members who have a day off for a given Azure DevOps project.
   *
   * @param project - The Backstage entity representing the Azure DevOps project.
   * @returns A promise resolving to an array of `ADOUser` objects representing team members with their day-off details.
   */
  async getTeamMembersDayOff(
    project: Entity,
  ): ReturnType<ICommonAdoService['getTeamMembersDayOff']> {
    const projectName = getProjectName(project.metadata?.links ?? []);

    const url = `${await this.discoveryApi.getBaseUrl(
      this.azDevOpsPluginKey,
    )}/project/${projectName}/members/daysOff`;

    return await this.adoMiddleware.request(url);
  }

  /**
   * Fetches work items for a given Azure DevOps project based on a query.
   *
   * @param project - The Backstage entity representing the Azure DevOps project.
   * @param limit - The maximum number of work items to fetch. Defaults to 50.
   * @returns A promise resolving to an array of `WorkItem` objects representing the work items fetched.
   */
  async getWorkItemsByQuery(
    project: Entity,
    limit = 50,
  ): ReturnType<ICommonAdoService['getWorkItemsByQuery']> {
    const projectName = getProjectName(project.metadata?.links ?? []);
    const team = `${projectName} team`;
    const url = `${await this.discoveryApi.getBaseUrl(
      this.azDevOpsPluginKey,
    )}/project/${projectName}/team/${encodeURI(team)}/workitems/${limit}`;

    return await this.adoMiddleware.request(url, {
      method: 'POST',
    });
  }
}

/**
 * Extracts the Azure DevOps project name from the provided entity links.
 *
 * @param links - An array of entity links from the Backstage entity metadata.
 * @returns The extracted project name, or an empty string if no matching Azure DevOps link is found.
 *
 * @remarks
 * This function scans for Azure DevOps board links and parses the project name from the URL path.
 * If the project name ends with 'team', that suffix is removed to return the base project name.
 */
function getProjectName(links: EntityLink[]): string {
  const azureBoard = links.find(item => item.url.includes('dev.azure.com'));
  if (azureBoard) {
    const match = azureBoard.url.match(/\/board\/t\/([^/]+)/);
    let extractedString = match ? match[1] : null;
    if (extractedString && extractedString.toLowerCase().endsWith('team')) {
      extractedString = extractedString.slice(0, -4);
    }
    return extractedString ? decodeURIComponent(extractedString).trimEnd() : '';
  }
  return '';
}
